import { useAuth } from '../contexts/AuthContext';

/**
 * Hook for subscription-based feature gating
 * Returns subscription status and helper functions
 */
export const useSubscription = () => {
  const { profile } = useAuth();

  const normalizePlan = (plan, options = {}) => {
    if (!plan) return 'free';
    const raw = String(plan);
    const val = raw.toLowerCase();

    // Handle known Whop plan IDs (monthly/annual)
    if (raw === 'plan_DBXVLNLTqBIYK' || raw === 'plan_2nQAUoWggHLBk') return 'pro';
    if (raw === 'plan_eXbZqIxnR13uz' || raw === 'plan_auyU5uFXjcyIE') return 'elite';
    // UGC ladder - add all known SKUs; fallback to creator if status is active
    if (raw === 'plan_5BLztZ9jC4KzK' || raw === 'plan_noxLXIJbAgqD3') return 'creator';
    if (raw === 'plan_0BaO8IscYTvfS' || raw === 'plan_Q9XYlNHreWOJU') return 'creator_elite';
    if (raw === 'plan_oCcNRWEpF7Go8') return 'founder';
    if (val.includes('creator_elite')) return 'creator_elite';
    if (val.includes('creator')) return 'creator';
    if (val.includes('founder')) return 'founder';
    if (val.includes('ugc')) return 'creator';

    // If plan is unrecognized but the caller passed status=active and product=ugc, grant creator access
    if (options.product === 'ugc' && options.status === 'active') return 'creator';

    if (val.includes('elite')) return 'elite';
    if (val.includes('team')) return 'team';
    if (val.includes('pro') || val.includes('premium')) return 'pro';
    return 'free';
  };

  const subscription = {
    status: profile?.subscription_status || 'inactive',
    plan: normalizePlan(profile?.subscription_plan),
    cancelAtPeriodEnd: profile?.cancel_at_period_end || false,
  };

  // Coaching tier (core app)
  const tier = normalizePlan(profile?.subscription_tier || profile?.subscription_plan);
  // UGC tier (separate ladder). If plan is unknown but ugc_status is active, treat as creator to avoid false locks.
  const ugcTier = normalizePlan(profile?.ugc_tier || profile?.ugc_plan || (profile?.ugc_status === 'active' ? 'creator' : null), {
    product: 'ugc',
    status: profile?.ugc_status,
  });

  const isActive = subscription.status === 'active';
  const isPro = isActive && tier === 'pro';
  const isElite = isActive && (tier === 'elite' || tier === 'team');
  const isTeam = isActive && tier === 'team';
  const isFree = !isActive || tier === 'free';

  /**
   * Check if user has access to a specific feature
   * @param {string} feature - Feature name to check
   * @returns {boolean} Access granted
   */
  const hasAccess = (feature) => {
    const featureMap = {
      // Free features (everyone)
      dailyDrill: true,
      navigation: true,
      
      // Pro features
      allTraining: isPro || isElite,
      advancedTraining: isPro || isElite,
      analytics: isPro || isElite,
      unlimitedDrills: isPro || isElite,
      microLessons: isPro || isElite,
      scriptLab: isPro || isElite,
      objectionDojo: isPro || isElite,
      megaTrainer: isPro || isElite,
      
      // Elite features
      conversationAnalyzer: isElite,
      aiRoleplay: isElite,
      
      // Team features
      teamManagement: isTeam,
      teamAnalytics: isTeam,
      inviteMembers: isTeam,
      customContent: isTeam,
      multiSeat: isTeam,
    };

    return featureMap[feature] ?? false;
  };

  /**
   * Get upgrade message for locked features
   * @param {string} feature - Feature name
   * @returns {string} Upgrade message
   */
  const getUpgradeMessage = (feature) => {
    const messages = {
      allTraining: 'Upgrade to Pro to unlock all training modules and practice features',
      advancedTraining: 'Upgrade to Pro to unlock advanced training modules',
      analytics: 'Upgrade to Pro to track your performance with detailed analytics',
      unlimitedDrills: 'Upgrade to Pro for unlimited practice drills',
      microLessons: 'Upgrade to Pro to access micro-lessons and coaching concepts',
      scriptLab: 'Upgrade to Pro to access the script library',
      objectionDojo: 'Upgrade to Pro to practice objection handling',
      conversationAnalyzer: 'Upgrade to Elite to analyze your sales conversations with AI feedback',
      aiRoleplay: 'Upgrade to Elite to practice with AI roleplay scenarios',
      teamManagement: 'Upgrade to Team plan to manage your sales team',
      teamAnalytics: 'Upgrade to Team plan to view team analytics and leaderboards',
      inviteMembers: 'Upgrade to Team plan to invite team members',
      customContent: 'Upgrade to Team plan to create custom training content',
    };

    return messages[feature] ?? 'Upgrade to unlock this feature';
  };

  return {
    subscription,
    tier,
    ugcTier,
    isActive,
    isPro,
    isElite,
    isTeam,
    isFree,
    hasAccess,
    getUpgradeMessage,
  };
};
